import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Briefcase, Users, Plus, Edit2, Trash2, Eye, 
  RefreshCw, ChevronLeft, ChevronRight, FileText, Download,
  CheckCircle2, XCircle, Clock, Search
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentCareers() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [jobForm, setJobForm] = useState({
    title: "",
    department: "",
    type: "internship",
    work_mode: "remote",
    location: "Remote",
    description: "",
    responsibilities: "",
    requirements: "",
    preferred_qualifications: "",
    benefits: "",
    salary_min: "",
    salary_max: "",
    salary_currency: "USD",
    salary_period: "yearly",
    show_salary: true,
    experience_level: "entry",
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, [page, statusFilter]);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    setLoading(true);
    try {
      const [statsRes, jobsRes, appsRes] = await Promise.all([
        axios.get(`${API}/agent/careers/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agent/careers/jobs`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agent/careers/applications`, {
          params: { page, status: statusFilter || undefined },
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setApplications(appsRes.data.applications);
      setTotalPages(appsRes.data.pages);
    } catch (error) {
      toast.error("Failed to load careers data");
    } finally {
      setLoading(false);
    }
  };

  const seedJobs = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/careers/seed-jobs`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to seed jobs");
    }
  };

  const handleCreateJob = () => {
    setIsEditing(false);
    setJobForm({
      title: "",
      department: "",
      type: "internship",
      work_mode: "remote",
      location: "Remote",
      description: "",
      responsibilities: "",
      requirements: "",
      preferred_qualifications: "",
      benefits: "",
      salary_min: "",
      salary_max: "",
      salary_currency: "USD",
      salary_period: "yearly",
      show_salary: true,
      experience_level: "entry",
      is_active: true
    });
    setJobDialogOpen(true);
  };

  const handleEditJob = (job) => {
    setIsEditing(true);
    setSelectedJob(job);
    setJobForm({
      title: job.title,
      department: job.department,
      type: job.type,
      work_mode: job.work_mode || "remote",
      location: job.location,
      description: job.description,
      responsibilities: job.responsibilities?.join("\n") || "",
      requirements: job.requirements?.join("\n") || "",
      preferred_qualifications: job.preferred_qualifications?.join("\n") || "",
      benefits: job.benefits?.join("\n") || "",
      salary_min: job.salary_min || "",
      salary_max: job.salary_max || "",
      salary_currency: job.salary_currency || "USD",
      salary_period: job.salary_period || "yearly",
      show_salary: job.show_salary !== false,
      experience_level: job.experience_level || "entry",
      is_active: job.is_active
    });
    setJobDialogOpen(true);
  };

  const handleSaveJob = async () => {
    const token = localStorage.getItem("agent_token");
    const data = {
      ...jobForm,
      responsibilities: jobForm.responsibilities.split("\n").filter(r => r.trim()),
      requirements: jobForm.requirements.split("\n").filter(r => r.trim()),
      preferred_qualifications: jobForm.preferred_qualifications.split("\n").filter(p => p.trim()),
      benefits: jobForm.benefits.split("\n").filter(b => b.trim()),
      salary_min: jobForm.salary_min ? parseInt(jobForm.salary_min) : null,
      salary_max: jobForm.salary_max ? parseInt(jobForm.salary_max) : null
    };

    try {
      if (isEditing) {
        await axios.put(`${API}/agent/careers/jobs/${selectedJob.job_id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Job posting updated");
      } else {
        await axios.post(`${API}/agent/careers/jobs`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Job posting created");
      }
      setJobDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save job");
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!confirm("Are you sure you want to delete this job posting?")) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/careers/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job posting deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleViewApplication = async (appId) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/careers/applications/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedApp(response.data);
      setAppDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load application");
    }
  };

  const handleUpdateStatus = async (status, notes = "") => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(
        `${API}/agent/careers/applications/${selectedApp.application_id}/status`,
        null,
        {
          params: { status, notes: notes || undefined },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`Status updated to ${status}`);
      setSelectedApp(prev => ({ ...prev, status }));
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "new":
        return <Badge className="bg-blue-100 text-blue-800">New</Badge>;
      case "reviewing":
        return <Badge className="bg-amber-100 text-amber-800">Reviewing</Badge>;
      case "interviewed":
        return <Badge className="bg-purple-100 text-purple-800">Interviewed</Badge>;
      case "hired":
        return <Badge className="bg-green-100 text-green-800">Hired</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "commission":
        return <Badge className="bg-green-100 text-green-800">Commission</Badge>;
      case "internship":
        return <Badge className="bg-blue-100 text-blue-800">Internship</Badge>;
      case "full_time":
        return <Badge className="bg-purple-100 text-purple-800">Full-Time</Badge>;
      case "part_time":
        return <Badge className="bg-amber-100 text-amber-800">Part-Time</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getWorkModeBadge = (mode) => {
    switch (mode) {
      case "remote":
        return <Badge variant="outline" className="border-teal-500 text-teal-700">Remote</Badge>;
      case "onsite":
        return <Badge variant="outline" className="border-orange-500 text-orange-700">On-site</Badge>;
      case "hybrid":
        return <Badge variant="outline" className="border-indigo-500 text-indigo-700">Hybrid</Badge>;
      default:
        return null;
    }
  };

  const formatSalary = (job) => {
    if (!job.salary_min && !job.salary_max) return null;
    
    const format = (amount) => {
      if (amount >= 1000) {
        return `$${(amount / 1000).toFixed(0)}k`;
      }
      return `$${amount}`;
    };
    
    const period = job.salary_period === "yearly" ? "/yr" : job.salary_period === "monthly" ? "/mo" : "/hr";
    
    if (job.salary_min && job.salary_max) {
      return `${format(job.salary_min)} - ${format(job.salary_max)}${period}`;
    } else if (job.salary_min) {
      return `From ${format(job.salary_min)}${period}`;
    } else {
      return `Up to ${format(job.salary_max)}${period}`;
    }
  };

  return (
    <AgentLayout title="Careers Management">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
              Careers Management
            </h1>
            <p className="text-muted-foreground">Manage job postings and applications</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={seedJobs}>
              Seed Sample Jobs
            </Button>
            <Button onClick={handleCreateJob}>
              <Plus className="h-4 w-4 mr-2" />
              Add Job
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.active_jobs}</p>
                    <p className="text-sm text-muted-foreground">Active Jobs</p>
                  </div>
                  <Briefcase className="h-8 w-8 text-primary opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.total_applications}</p>
                    <p className="text-sm text-muted-foreground">Total Applications</p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.new_applications}</p>
                    <p className="text-sm text-muted-foreground">New Applications</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.hired}</p>
                    <p className="text-sm text-muted-foreground">Hired</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="applications">
          <TabsList>
            <TabsTrigger value="applications">Applications</TabsTrigger>
            <TabsTrigger value="jobs">Job Postings</TabsTrigger>
          </TabsList>

          <TabsContent value="applications" className="mt-4">
            {/* Filters */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <Button
                variant={statusFilter === "" ? "default" : "outline"}
                size="sm"
                onClick={() => { setStatusFilter(""); setPage(1); }}
              >
                All
              </Button>
              <Button
                variant={statusFilter === "new" ? "default" : "outline"}
                size="sm"
                onClick={() => { setStatusFilter("new"); setPage(1); }}
              >
                New
              </Button>
              <Button
                variant={statusFilter === "reviewing" ? "default" : "outline"}
                size="sm"
                onClick={() => { setStatusFilter("reviewing"); setPage(1); }}
              >
                Reviewing
              </Button>
              <Button
                variant={statusFilter === "interviewed" ? "default" : "outline"}
                size="sm"
                onClick={() => { setStatusFilter("interviewed"); setPage(1); }}
              >
                Interviewed
              </Button>
              <Button
                variant={statusFilter === "hired" ? "default" : "outline"}
                size="sm"
                onClick={() => { setStatusFilter("hired"); setPage(1); }}
              >
                Hired
              </Button>
            </div>

            {/* Applications Table */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : applications.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No applications found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr className="text-left">
                          <th className="p-4 font-medium">Applicant</th>
                          <th className="p-4 font-medium">Position</th>
                          <th className="p-4 font-medium">Status</th>
                          <th className="p-4 font-medium">Date</th>
                          <th className="p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {applications.map((app) => (
                          <tr key={app.application_id} className="hover:bg-muted/30">
                            <td className="p-4">
                              <p className="font-medium">{app.full_name}</p>
                              <p className="text-sm text-muted-foreground">{app.email}</p>
                            </td>
                            <td className="p-4">{app.job_title}</td>
                            <td className="p-4">{getStatusBadge(app.status)}</td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {new Date(app.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewApplication(app.application_id)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 p-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 py-2 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="jobs" className="mt-4">
            <div className="grid gap-4">
              {jobs.map((job) => (
                <Card key={job.job_id} data-testid={`admin-job-${job.job_id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {job.title}
                          {!job.is_active && <Badge variant="secondary">Inactive</Badge>}
                        </CardTitle>
                        <CardDescription>
                          {job.department} • {job.location} • {job.application_count || 0} applications
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        {getTypeBadge(job.type)}
                        {getWorkModeBadge(job.work_mode)}
                        <Button variant="ghost" size="icon" onClick={() => handleEditJob(job)} data-testid={`edit-job-${job.job_id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteJob(job.job_id)} data-testid={`delete-job-${job.job_id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2">{job.description}</p>
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      {job.experience_level && (
                        <span className="text-xs text-muted-foreground capitalize">{job.experience_level.replace('_', ' ')} level</span>
                      )}
                      {formatSalary(job) && (
                        <span className="text-sm font-medium text-primary">{formatSalary(job)}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Job Dialog */}
      <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Job Posting" : "Create Job Posting"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label>Job Title *</Label>
              <Input
                value={jobForm.title}
                onChange={(e) => setJobForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Growth & Marketing Intern"
                data-testid="job-title-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department *</Label>
                <Input
                  value={jobForm.department}
                  onChange={(e) => setJobForm(prev => ({ ...prev, department: e.target.value }))}
                  placeholder="e.g., Marketing"
                  data-testid="job-department-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Job Type *</Label>
                <Select
                  value={jobForm.type}
                  onValueChange={(value) => setJobForm(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger data-testid="job-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commission">Commission-Based</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                    <SelectItem value="full_time">Full-Time</SelectItem>
                    <SelectItem value="part_time">Part-Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Work Mode</Label>
                <Select
                  value={jobForm.work_mode}
                  onValueChange={(value) => setJobForm(prev => ({ ...prev, work_mode: value }))}
                >
                  <SelectTrigger data-testid="job-work-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="onsite">On-site</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Experience Level</Label>
                <Select
                  value={jobForm.experience_level}
                  onValueChange={(value) => setJobForm(prev => ({ ...prev, experience_level: value }))}
                >
                  <SelectTrigger data-testid="job-experience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entry">Entry Level</SelectItem>
                    <SelectItem value="mid">Mid Level</SelectItem>
                    <SelectItem value="senior">Senior</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="executive">Executive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                value={jobForm.location}
                onChange={(e) => setJobForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Remote - US, New York, NY"
                data-testid="job-location-input"
              />
            </div>

            {/* Salary Section */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Salary Information</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="show_salary"
                    checked={jobForm.show_salary}
                    onChange={(e) => setJobForm(prev => ({ ...prev, show_salary: e.target.checked }))}
                  />
                  <Label htmlFor="show_salary" className="text-sm">Show on listing</Label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Minimum</Label>
                  <Input
                    type="number"
                    value={jobForm.salary_min}
                    onChange={(e) => setJobForm(prev => ({ ...prev, salary_min: e.target.value }))}
                    placeholder="e.g., 50000"
                    data-testid="job-salary-min-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Maximum</Label>
                  <Input
                    type="number"
                    value={jobForm.salary_max}
                    onChange={(e) => setJobForm(prev => ({ ...prev, salary_max: e.target.value }))}
                    placeholder="e.g., 80000"
                    data-testid="job-salary-max-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Currency</Label>
                  <Select
                    value={jobForm.salary_currency}
                    onValueChange={(value) => setJobForm(prev => ({ ...prev, salary_currency: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Period</Label>
                  <Select
                    value={jobForm.salary_period}
                    onValueChange={(value) => setJobForm(prev => ({ ...prev, salary_period: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yearly">Per Year</SelectItem>
                      <SelectItem value="monthly">Per Month</SelectItem>
                      <SelectItem value="hourly">Per Hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Job Description *</Label>
              <Textarea
                rows={4}
                value={jobForm.description}
                onChange={(e) => setJobForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the role and what the candidate will be doing..."
                data-testid="job-description-textarea"
              />
            </div>

            {/* Responsibilities */}
            <div className="space-y-2">
              <Label>Key Responsibilities (one per line)</Label>
              <Textarea
                rows={4}
                value={jobForm.responsibilities}
                onChange={(e) => setJobForm(prev => ({ ...prev, responsibilities: e.target.value }))}
                placeholder="Design and implement new features&#10;Collaborate with the team&#10;Write clean, maintainable code..."
                data-testid="job-responsibilities-textarea"
              />
            </div>

            {/* Requirements */}
            <div className="space-y-2">
              <Label>Requirements (one per line)</Label>
              <Textarea
                rows={4}
                value={jobForm.requirements}
                onChange={(e) => setJobForm(prev => ({ ...prev, requirements: e.target.value }))}
                placeholder="3+ years of experience&#10;Strong communication skills&#10;Proficiency in..."
                data-testid="job-requirements-textarea"
              />
            </div>

            {/* Preferred Qualifications */}
            <div className="space-y-2">
              <Label>Preferred Qualifications (one per line)</Label>
              <Textarea
                rows={3}
                value={jobForm.preferred_qualifications}
                onChange={(e) => setJobForm(prev => ({ ...prev, preferred_qualifications: e.target.value }))}
                placeholder="Previous experience in...&#10;Certification in..."
                data-testid="job-preferred-textarea"
              />
            </div>

            {/* Benefits */}
            <div className="space-y-2">
              <Label>Benefits (one per line)</Label>
              <Textarea
                rows={4}
                value={jobForm.benefits}
                onChange={(e) => setJobForm(prev => ({ ...prev, benefits: e.target.value }))}
                placeholder="Health insurance&#10;Flexible hours&#10;Remote work..."
                data-testid="job-benefits-textarea"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={jobForm.is_active}
                onChange={(e) => setJobForm(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              <Label htmlFor="is_active">Active (visible on careers page)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJobDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveJob} data-testid="save-job-btn">Save Job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Dialog */}
      <Dialog open={appDialogOpen} onOpenChange={setAppDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedApp && (
            <>
              <DialogHeader>
                <DialogTitle>Application Details</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold">{selectedApp.full_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedApp.email}</p>
                    <p className="text-sm text-muted-foreground">{selectedApp.phone}</p>
                  </div>
                  {getStatusBadge(selectedApp.status)}
                </div>
                
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Applied for:</p>
                  <p>{selectedApp.job_title}</p>
                </div>

                {selectedApp.linkedin_url && (
                  <div>
                    <p className="text-sm font-medium">LinkedIn:</p>
                    <a href={selectedApp.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {selectedApp.linkedin_url}
                    </a>
                  </div>
                )}

                {selectedApp.portfolio_url && (
                  <div>
                    <p className="text-sm font-medium">Portfolio:</p>
                    <a href={selectedApp.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {selectedApp.portfolio_url}
                    </a>
                  </div>
                )}

                {selectedApp.cover_letter && (
                  <div>
                    <p className="text-sm font-medium mb-2">Cover Letter:</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted p-3 rounded-lg">
                      {selectedApp.cover_letter}
                    </p>
                  </div>
                )}

                {selectedApp.resume_url && (
                  <div>
                    <Button variant="outline" asChild>
                      <a href={selectedApp.resume_url} download="resume">
                        <Download className="h-4 w-4 mr-2" />
                        Download Resume
                      </a>
                    </Button>
                  </div>
                )}

                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Update Status:</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={selectedApp.status === "reviewing" ? "default" : "outline"}
                      onClick={() => handleUpdateStatus("reviewing")}
                    >
                      Reviewing
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedApp.status === "interviewed" ? "default" : "outline"}
                      onClick={() => handleUpdateStatus("interviewed")}
                    >
                      Interviewed
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedApp.status === "hired" ? "default" : "outline"}
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleUpdateStatus("hired")}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Hire
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedApp.status === "rejected" ? "default" : "outline"}
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => handleUpdateStatus("rejected")}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
